"use client";

import { OTPForm } from "@/components/auth/otp/otp-form";
import { SocialAuthButtons } from "@/components/auth/common/social-auth-buttons";
import { LineLabel } from "@/components/common/line-label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { H2 } from "@/components/ui/heading";
import { Icon } from "@/components/ui/icon";
import { Input } from "@/components/ui/input";
import { useCurrentUser } from "@/hooks/use-current-user";
import { useErrorFormatter } from "@/hooks/use-error-formatter";
import { api } from "@/lib/api-client";
import { mediaQuery } from "@/lib/media-query";
import { standardSchemaResolver } from "@hookform/resolvers/standard-schema";
import type { UserOTPKindType } from "@repo/database";
import { AlertTriangleIcon } from "lucide-react";
import { useTranslations } from "next-intl";
import { useEffect, useRef, useState } from "react";
import { type SubmitHandler, useForm } from "react-hook-form";
import { useMediaQuery } from "usehooks-ts";
import { z } from "zod/v4";

type Step = "email" | "otp" | "saving" | "done";

const emailSchema = z.object({
  email: z.string().email(),
});

type EmailFormValues = z.infer<typeof emailSchema>;

export type SaveSearchAuthDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onAuthenticated: () => void | Promise<void>;
};

export function SaveSearchAuthDialog({
  open,
  onOpenChange,
  onAuthenticated,
}: SaveSearchAuthDialogProps) {
  const t = useTranslations();
  const isDesktop = useMediaQuery(mediaQuery.sm);
  const { user, loaded, reloadUser } = useCurrentUser();
  const { setApiErrorsToForm } = useErrorFormatter();

  const [step, setStep] = useState<Step>("email");
  const [email, setEmail] = useState("");
  const [otpKind, setOtpKind] = useState<UserOTPKindType>("CREATE_ACCOUNT");
  const dialogCloseRef = useRef<HTMLButtonElement>(null);

  const createAccountMutation =
    api.auth.createPasswordlessAccount.useMutation();
  const checkEmailMutation = api.auth.checkEmail.useMutation();
  const sendLoginOTPMutation = api.auth.sendLoginOTP.useMutation();

  const form = useForm<EmailFormValues>({
    resolver: standardSchemaResolver(emailSchema),
    defaultValues: { email: "" },
  });

  useEffect(() => {
    if (open && user && loaded && (step === "otp" || step === "email")) {
      setStep("saving");
      onAuthenticated();
    }
  }, [user, loaded]);

  function handleOpenChange(nextOpen: boolean) {
    onOpenChange(nextOpen);
    if (!nextOpen) {
      setStep("email");
      setEmail("");
      setOtpKind("CREATE_ACCOUNT");
      form.reset();
    }
  }

  const onSubmitEmail: SubmitHandler<EmailFormValues> = async ({
    email: emailValue,
  }) => {
    try {
      const { exists } = await checkEmailMutation.mutateAsync({
        email: emailValue,
        doNotSendToken: true,
      });

      setEmail(emailValue);

      if (exists) {
        await sendLoginOTPMutation.mutateAsync({ email: emailValue });
        setOtpKind("LOGIN");
        setStep("otp");
        return;
      }

      await createAccountMutation.mutateAsync({ email: emailValue });
      setOtpKind("CREATE_ACCOUNT");
      setStep("otp");
    } catch (err) {
      console.error(err);
      setApiErrorsToForm(err, form);
    }
  };

  async function onOTPDone() {
    if (!user || !loaded) {
      await reloadUser();
    }
  }

  const titleText =
    step === "email"
      ? "Save this search"
      : step === "otp"
        ? t("saas.auth.otp.title")
        : step === "saving"
          ? "Saving..."
          : "Saved!";

  const content = (
    <>
      {step === "email" && (
        <div className="flex flex-col gap-5">
          <p className="text-sm text-muted-foreground text-pretty">
            Enter your email to save this search and get notified when new
            listings match.
          </p>

          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(onSubmitEmail)}
              className="space-y-4"
            >
              {form.formState.isSubmitted &&
                form.formState.errors.root?.message && (
                  <Alert variant="destructive">
                    <AlertTriangleIcon className="size-6" />
                    <AlertDescription>
                      {form.formState.errors.root.message}
                    </AlertDescription>
                  </Alert>
                )}

              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      {t("saas.auth.home.form.fields.email.label")}
                    </FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        placeholder={t(
                          "saas.auth.home.form.fields.email.placeholder",
                        )}
                        autoComplete="email"
                        autoFocus
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <Button
                className="h-12 w-full bg-foreground text-background hover:bg-foreground/90"
                type="submit"
                disabled={
                  form.formState.isSubmitting ||
                  createAccountMutation.isPending ||
                  checkEmailMutation.isPending ||
                  sendLoginOTPMutation.isPending
                }
              >
                <Icon icon="Bell" className="mr-2 h-4 w-4" />
                Save search &amp; get alerts
              </Button>
            </form>
          </Form>

          <LineLabel>
            <div className="bg-background px-2 text-muted-foreground">
              {t("saas.auth.social.or")}
            </div>
          </LineLabel>

          <SocialAuthButtons />

          <p className="text-xs text-muted-foreground text-center">
            No password needed. We&apos;ll verify your email with a quick code.
          </p>
        </div>
      )}

      {step === "otp" && (
        <div className="flex flex-col gap-4">
          <p className="text-sm text-muted-foreground">
            We sent a verification code to{" "}
            <span className="font-medium text-foreground">{email}</span>
          </p>
          <OTPForm
            identifier={email.trim().toLowerCase()}
            kind={otpKind}
            callbackUrl="/"
            onDone={onOTPDone}
          />
        </div>
      )}

      {step === "saving" && (
        <div className="flex flex-col items-center gap-4 py-8">
          <div className="size-8 border-2 border-foreground/20 border-t-foreground rounded-full animate-spin" />
          <p className="text-sm text-muted-foreground">
            Saving your search...
          </p>
        </div>
      )}
    </>
  );

  const titleElement = (
    <H2 className="flex items-center gap-2 text-left">
      {step === "otp" && (
        <button
          type="button"
          onClick={() => setStep("email")}
          className="rounded-full p-1 hover:bg-muted"
        >
          <Icon icon="ChevronLeft" className="h-4 w-4" />
        </button>
      )}
      {titleText}
    </H2>
  );

  if (isDesktop) {
    return (
      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogClose ref={dialogCloseRef} />
        <DialogContent closable={step !== "otp"} className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle asChild>{titleElement}</DialogTitle>
          </DialogHeader>
          {content}
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Drawer
      open={open}
      onOpenChange={handleOpenChange}
      dismissible={step !== "otp"}
    >
      <DrawerContent>
        <div className="overflow-y-auto px-4 pb-[calc(env(safe-area-inset-bottom)+1.5rem)]">
          <DrawerHeader className="px-0">
            <DrawerTitle asChild>{titleElement}</DrawerTitle>
          </DrawerHeader>
          {content}
        </div>
      </DrawerContent>
    </Drawer>
  );
}

SaveSearchAuthDialog.displayName = "SaveSearchAuthDialog";
